
export enum Relationship {
  SPOUSE = 'Spouse',
  CHILD = 'Child',
  PARENT = 'Parent',
  SIBLING = 'Sibling',
  OTHER = 'Other'
}

export enum AssetCategory {
  // Banking & Deposit (RBI)
  SAVINGS = 'Savings Account',
  CURRENT = 'Current Account',
  FD = 'Fixed Deposit (FD)',
  RD = 'Recurring Deposit (RD)',
  LOCKER = 'Safe Deposit Locker',
  COOP_BANK = 'Cooperative Bank',
  POST_OFFICE_SAVINGS = 'Post Office Savings Account',

  // INVESTMENTS & WEALTH (SEBI)
  MUTUAL_FUND = 'Mutual Funds (each folio)',
  DEMAT = 'Demat Account (CDSL / NSDL)',
  TRADING = 'Trading Account',
  BONDS = 'Bonds & Debentures',
  SGB = 'Sovereign Gold Bonds (SGB)',
  ETFS = 'ETFs',
  REITS_INVITS = 'REITs & InvITs',

  // INSURANCE (IRDAI)
  LIFE_INSURANCE = 'Life Insurance Policies',
  TERM_INSURANCE = 'Term Insurance',
  ENDOWMENT = 'Endowment Plans',
  ULIPS = 'ULIPs',
  PENSION_ANNUITY = 'Pension / Annuity Policies',

  // RETIREMENT & PENSION (PFRDA / EPFO)
  EPF = 'EPF (Provident Fund)',
  EPS = 'EPS (Pension Scheme)',
  NPS = 'NPS (National Pension System)',
  SUPERANNUATION = 'Superannuation Funds',
  GRATUITY = 'Gratuity (Form F)',

  // GOVERNMENT & SMALL SAVINGS
  POST_OFFICE_SCHEMES = 'Post Office Schemes',
  NSC = 'NSC (National Savings Certificate)',
  KVP = 'KVP (Kisan Vikas Patra)',
  PPF = 'PPF (Public Provident Fund)',
  SUKANYA_SAMRIDDHI = 'Sukanya Samriddhi Yojana',

  // EMPLOYER / CORPORATE BENEFITS
  SALARY_BENEFITS = 'Salary Account Benefits',
  GROUP_INSURANCE = 'Group Insurance',
  GROUP_HEALTH = 'Group Health Cover',
  ESOPS_RSUS = 'ESOPs / RSUs',
  LEAVE_ENCASHMENT = 'Leave Encashment',
  DEATH_COMPENSATION = 'Death-in-Service Compensation',

  // FINTECH & DIGITAL ASSETS
  WALLETS = 'Wallets (Paytm, PhonePe etc.)',
  DIGITAL_GOLD = 'Digital Gold',
  FINTECH_APPS = 'Online Investment Apps',
  CRYPTO = 'Crypto Exchanges',
  NEO_BANKS = 'Neo-banks',

  // PROPERTY & REAL ASSETS
  CHS = 'Cooperative Housing Societies',
  APARTMENTS = 'Apartments / Builder Registrations',
  LAND_RECORDS = 'Land Records (state-specific)',
  COMMERCIAL_PROPERTY = 'Commercial Properties',
  
  // Legacy / Fallback
  INVESTMENT = 'Investment / Stocks',
  PROPERTY = 'Property / Real Estate',
  EMPLOYER = 'Employer Benefits',
  INSURANCE = 'Insurance',
  RETIREMENT = 'Retirement'
}

export interface CategoryInfo {
  id: string;
  sector_id: string;
  name: string;
  display_name: string;
}

export interface Sector {
  id: string;
  name: string;
  categories: CategoryInfo[];
}

export type UserRole = 'owner' | 'nominee';

export type LegalPageType = 'features' | 'security' | 'compliance' | 'privacy' | 'terms' | 'faq' | 'contact';

export interface UserProfile {
  id?: string; // Supabase UUID
  name: string;
  email: string;
  avatar?: string;
  isGoogleLinked: boolean;
  isVerified: boolean;
  role?: UserRole;
  phone?: string;
  address?: string;
  correspondenceAddress?: string;
  dob?: string;
  gender?: string;
  nationality?: string;
  maritalStatus?: string;
  panLast5?: string;
  aadhaarLast4?: string;
  passportNumber?: string;
  voterId?: string;
  occupation?: string;
  employer?: string;
  annualIncomeRange?: string;
}

export interface Institution {
  id: string;
  name: string;
  color: string;
  logoSvg: string;
  isVerified: boolean;
}

export interface Person {
  id: string;
  fullName: string;
  relationship: Relationship;
  email: string;
  targetShare: number;
  dob?: string;
  isMinor?: boolean;
  priorityOrder?: string;
  assetMappingPreferences?: string;
  guardianName?: string;
  gender?: string;
  pan?: string;
  aadhaar?: string;
  passport?: string;
  mobile?: string;
  address?: string;
  guardianRelationship?: string;
  guardianContact?: string;
  guardianPan?: string;
}

export interface Allocation {
  personId: string;
  percentage: number;
  nomineeRole?: 'Primary' | 'Secondary' | 'Joint';
  nomineeType?: 'Beneficial' | 'Collector / Trustee';
}

export interface BankLogEntry {
  id: string;
  timestamp: string;
  event: string;
  status: 'Success' | 'Warning' | 'Info';
}

export interface Asset {
  id: string;
  name: string;
  category: AssetCategory | string;
  value: number;
  allocations: Allocation[];
  institution?: string;
  claimPath?: 'Online' | 'Physical Visit' | 'Hybrid';
  contactAuthority?: string;
  estimatedTimeline?: string;
  status?: 'Verified' | 'Outdated' | 'Missing' | 'Minor Involved';
  accountType?: string;
  assetClass?: 'Equity' | 'Debt' | 'Gold' | 'Hybrid' | 'Others';
  customerId?: string;
  ifscCode?: string;
  branchName?: string;
  branchDetails?: string;
  modeOfOperation?: 'Single' | 'Joint' | 'Either or Survivor' | 'Former or Survivor';
  hasDebitCard?: boolean;
  hasNetBanking?: boolean;
  bankLogs?: BankLogEntry[];
  nominationStatus?: boolean;
  lastUpdated?: string;
  // Specific Metadata
  policyStatus?: 'Active' | 'Lapsed';
  sumAssuredRange?: string;
  nomineeAvailability?: 'Yes' | 'Not supported';
  userInstructions?: string;
}

export interface AiAnalysisResult {
  isBalanced: boolean;
  message: string;
  suggestions: string[];
}

export interface NomineeDiscoveryItem {
  ownerName: string;
  ownerId: string;
  relationship: string;
  totalValueForNominee: number;
  assets: (Asset & { nomineePercentage: number; nomineeEstimatedValue: number; nomineeRole?: string; nomineeType?: string })[];
}

export interface FinancialProgram {
  id: string;
  name: string;
  type: string;
  status: string;
  logoColor: string;
}
